import {
  Control,
  FieldPath,
  FieldPathValue,
  useWatch,
  UseWatchProps,
} from 'react-hook-form';
import { useDebounceValue } from 'usehooks-ts';

type UseDebouncedWatchOptions<
  TFieldValues extends Record<string, unknown>,
  TName extends FieldPath<TFieldValues>,
> = Omit<UseWatchProps<TFieldValues>, 'name' | 'control'> & {
  name: TName;
  defaultValue?: FieldPathValue<TFieldValues, TName>;
  delay?: number;
  control: Control<TFieldValues>;
};

export const useDebouncedWatch = <
  TFieldValues extends Record<string, unknown>,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  defaultValue,
  delay = 500,
  ...restOptions
}: UseDebouncedWatchOptions<TFieldValues, TName>): FieldPathValue<
  TFieldValues,
  TName
> => {
  const value = useWatch({
    control,
    name,
    defaultValue,
    ...restOptions,
  });

  const [debouncedValue] = useDebounceValue(value, delay);

  return debouncedValue;
};
